/**
 * 教师中心 - 试卷列表页面
 * 复用后台Papers的核心逻辑，但操作teacher_papers表
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Plus, Edit2, Eye, Trash2, Search, RefreshCw, XCircle, X, Check, Loader2 } from 'lucide-react';
import { formatDate, formatDateTimeWithSeconds } from '../../utils/timeUtils';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import QuizAnswerMode from '../quiz/QuizAnswerMode';
import { mapDbQuestionsToStandard } from '../../utils/questionMapper';

interface TeacherPaper {
  id: string;
  title: string;
  year: number | null;
  month: number | null;
  organization: string | null;
  category_type: string | null;
  level_text: string | null;
  paper_type: string | null;
  duration: number | null;
  is_published: boolean;
  is_free: boolean;
  status: string;
  single_choice_count: number | null;
  multi_choice_count: number | null;
  judge_count: number | null;
  coding_count: number | null;
  total_score: number | null;
  remark: string | null;
  created_at: string;
  updated_at: string | null;
}

export default function TeacherPapers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRecycleBinParam = searchParams.get('recycle') === 'true';

  const [papers, setPapers] = useState<TeacherPaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRecycleBin, setShowRecycleBin] = useState(isRecycleBinParam);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 监听URL参数变化，同步更新回收站状态
  useEffect(() => {
    setShowRecycleBin(searchParams.get('recycle') === 'true');
  }, [searchParams]);

  // 预览模态框状态
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPaperId, setPreviewPaperId] = useState<string | null>(null);
  const [previewPaperData, setPreviewPaperData] = useState<any>(null);
  const [previewQuestions, setPreviewQuestions] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // 切换状态 loading
  const [togglingPaperId, setTogglingPaperId] = useState<string | null>(null);

  // 备注编辑相关状态
  const [editingRemark, setEditingRemark] = useState<string | null>(null);
  const [remarkValue, setRemarkValue] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  // 切换试卷发布状态
  const handleToggleStatus = async (paper: TeacherPaper) => {
    setTogglingPaperId(paper.id);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'toggle_paper_status',
          paperId: paper.id,
        }),
      });

      const result = await response.json();
      if (result.success) {
        // 更新本地状态
        setPapers(prev => prev.map(p =>
          p.id === paper.id
            ? { ...p, is_published: result.data.is_published, status: result.data.status }
            : p
        ));
      } else {
        alert(result.message || '操作失败');
      }
    } catch (error) {
      console.error('切换状态失败:', error);
      alert('网络错误，请重试');
    } finally {
      setTogglingPaperId(null);
    }
  };

  // 检查用户权限并加载数据
  useEffect(() => {
    const loadData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role !== 'teacher' && profile?.role !== 'admin') {
        navigate('/');
        return;
      }

      loadPapers(user.id);
    };

    loadData();
  }, [navigate, showRecycleBin, searchTerm]);

  const loadPapers = async (userId: string) => {
    setLoading(true);
    try {
      let query = supabase
        .from('teacher_papers')
        .select('*')
        .order('created_at', { ascending: false });

      if (showRecycleBin) {
        // 回收站模式：只显示已删除的
        query = query.not('deleted_at', 'is', null);
      } else {
        // 正常模式：只显示未删除的
        query = query.is('deleted_at', null);
      }

      // 优先按created_by过滤，兼容created_by为null的旧数据
      if (userId) {
        query = query.or(`created_by.eq.${userId},created_by.is.null`);
      }

      // 应用搜索过滤（同时搜索标题和备注）
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        query = query.or(`title.ilike.%${term}%,remark.ilike.%${term}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('加载试卷失败:', error);
        return;
      }

      setPapers(data || []);
    } catch (error) {
      console.error('加载试卷异常:', error);
    } finally {
      setLoading(false);
    }
  };

  // 备注编辑相关函数
  const startEditRemark = (paper: TeacherPaper) => {
    setEditingRemark(paper.id);
    setRemarkValue(paper.remark || '');
  };

  const saveRemark = async (id: string) => {
    setSavingId(id);
    try {
      const { error } = await supabase
        .from('teacher_papers')
        .update({ remark: remarkValue, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        alert('保存失败: ' + error.message);
        return;
      }

      setPapers(papers.map(p => p.id === id ? { ...p, remark: remarkValue } : p));
    } finally {
      setSavingId(null);
      setEditingRemark(null);
    }
  };

  const handleRemarkKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') saveRemark(id);
  };

  const handleDelete = async (paperId: string) => {
    if (!confirm('确定要删除此试卷吗？')) return;

    try {
      const { error } = await supabase
        .from('teacher_papers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', paperId);

      if (error) {
        alert('删除失败: ' + error.message);
        return;
      }

      // 重新加载列表
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        loadPapers(user.id);
      }
    } catch (error) {
      alert('删除失败');
    }
  };

  // 恢复试卷
  const handleRestore = async (paperId: string) => {
    if (!confirm('确定要恢复此试卷吗？')) return;

    try {
      const { error } = await supabase
        .from('teacher_papers')
        .update({ deleted_at: null })
        .eq('id', paperId);

      if (error) {
        alert('恢复失败: ' + error.message);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        loadPapers(user.id);
      }
    } catch (error) {
      alert('恢复失败');
    }
  };

  // 彻底删除试卷
  const handlePermanentDelete = async (paperId: string) => {
    if (!confirm('确定要彻底删除此试卷吗？此操作不可恢复！')) return;

    try {
      const { error } = await supabase
        .from('teacher_papers')
        .delete()
        .eq('id', paperId);

      if (error) {
        alert('删除失败: ' + error.message);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        loadPapers(user.id);
      }
    } catch (error) {
      alert('删除失败');
    }
  };

  // 切换单个选中
  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // 全选/取消全选当前页
  const toggleSelectAll = () => {
    if (selectedIds.size === papers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(papers.map(p => p.id)));
    }
  };

  // 批量删除（正常模式）
  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) {
      alert('请先选择要删除的试卷');
      return;
    }
    if (!confirm(`确定删除选中的 ${selectedIds.size} 份试卷？删除后将移入回收站`)) return;

    try {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await supabase
          .from('teacher_papers')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', id);
      }
      setSelectedIds(new Set());
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        loadPapers(user.id);
      }
    } catch (error) {
      alert('批量删除失败');
    }
  };

  // 批量恢复（回收站模式）
  const handleBatchRestore = async () => {
    if (selectedIds.size === 0) {
      alert('请先选择要恢复的试卷');
      return;
    }
    if (!confirm(`确定恢复选中的 ${selectedIds.size} 份试卷？`)) return;

    try {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await supabase
          .from('teacher_papers')
          .update({ deleted_at: null })
          .eq('id', id);
      }
      setSelectedIds(new Set());
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        loadPapers(user.id);
      }
    } catch (error) {
      alert('批量恢复失败');
    }
  };

  // 批量彻底删除（回收站模式）
  const handleBatchPermanentDelete = async () => {
    if (selectedIds.size === 0) {
      alert('请先选择要删除的试卷');
      return;
    }
    if (!confirm(`确定彻底删除选中的 ${selectedIds.size} 份试卷？此操作不可恢复！`)) return;

    try {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await supabase
          .from('teacher_papers')
          .delete()
          .eq('id', id);
      }
      setSelectedIds(new Set());
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        loadPapers(user.id);
      }
    } catch (error) {
      alert('批量删除失败');
    }
  };

  const handleEdit = (paperId: string) => {
    navigate(`/teacher/paper-editor?edit=${paperId}`);
  };

  const handlePreview = async (paperId: string) => {
    setPreviewPaperId(paperId);
    setShowPreviewModal(true);
    setPreviewLoading(true);

    try {
      // 加载试卷基本信息
      const { data: paperData } = await supabase
        .from('teacher_papers')
        .select('*')
        .eq('id', paperId)
        .single();

      if (paperData) {
        setPreviewPaperData(paperData);

        // 加载题目数据
        const { data: questionsData } = await supabase
          .from('teacher_questions')
          .select('*')
          .eq('paper_id', paperId)
          .order('order_index', { ascending: true });

        setPreviewQuestions(questionsData || []);
      }
    } catch (error) {
      console.error('加载预览数据失败:', error);
      alert('加载试卷失败');
      setShowPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {showRecycleBin ? '试卷回收站' : '我的试卷'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {showRecycleBin ? '管理已删除的试卷' : '管理您创建和上传的试卷'}
          </p>
        </div>
        <div className="flex gap-3">
          {/* 批量操作按钮 */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm text-blue-700">已选择 {selectedIds.size} 项</span>
              {showRecycleBin ? (
                <>
                  <button
                    onClick={handleBatchRestore}
                    className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                  >
                    批量恢复
                  </button>
                  <button
                    onClick={handleBatchPermanentDelete}
                    className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  >
                    批量删除
                  </button>
                </>
              ) : (
                <button
                  onClick={handleBatchDelete}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  批量删除
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      {/* 筛选栏 - 只保留搜索框 */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          {/* 搜索框 - 扩展为同时搜索标题和备注 */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="搜索试卷标题或备注..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* 试卷列表 */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      ) : papers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {showRecycleBin ? '回收站为空' : '暂无试卷'}
          </h3>
          <p className="text-gray-500 mb-6">
            {showRecycleBin ? '没有已删除的试卷' : '开始创建或上传您的第一份试卷吧'}
          </p>
          {!showRecycleBin && (
            <button
              onClick={() => navigate('/teacher/paper-editor')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
            >
              <Plus size={20} />
              <span>新建试卷</span>
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    checked={papers.length > 0 && selectedIds.size === papers.length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">试卷标题</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px]">备注</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">最后修改时间</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {papers.map((paper) => (
                <tr key={paper.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(paper.id)}
                      onChange={() => toggleSelect(paper.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{paper.title}</div>
                  </td>
                  <td className="px-6 py-4 w-[200px]">
                    {editingRemark === paper.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={remarkValue}
                          onChange={(e) => setRemarkValue(e.target.value)}
                          onBlur={() => saveRemark(paper.id)}
                          onKeyDown={(e) => handleRemarkKeyDown(e, paper.id)}
                          className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                        {remarkValue && (
                          <button
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setRemarkValue('');
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors shrink-0"
                            title="清除备注"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {savingId === paper.id ? (
                          <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
                        ) : (
                          <Check className="w-4 h-4 text-green-500 shrink-0" />
                        )}
                      </div>
                    ) : (
                      <span onClick={() => startEditRemark(paper)} className="text-sm text-gray-600 cursor-pointer hover:text-blue-600 hover:underline block truncate" title={paper.remark || ''}>
                        {savingId === paper.id ? '保存中...' : (paper.remark || '-')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(paper)}
                      disabled={togglingPaperId === paper.id}
                      className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full transition-all hover:opacity-80 disabled:opacity-50 ${
                        paper.is_published
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                      title={paper.is_published ? '点击切换为草稿' : '点击切换为发布'}
                    >
                      {togglingPaperId === paper.id ? (
                        <RefreshCw size={12} className="animate-spin" />
                      ) : (
                        <RefreshCw size={12} />
                      )}
                      {paper.is_published ? '发布' : '草稿'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(paper.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTimeWithSeconds(paper.updated_at || paper.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {showRecycleBin ? (
                        <>
                          <button
                            onClick={() => handleRestore(paper.id)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="恢复"
                          >
                            <RefreshCw size={16} />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(paper.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="彻底删除"
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(paper.id)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="编辑"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handlePreview(paper.id)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="预览"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(paper.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="删除"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 试卷预览模态框 */}
      <AnimatePresence>
        {showPreviewModal && previewPaperData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => setShowPreviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <QuizAnswerMode
                  paper={{
                    id: previewPaperId || '',
                    title: previewPaperData.title || '试卷预览',
                    total_score: previewPaperData.total_score || 0,
                    category_type: previewPaperData.category_type || '',
                    description: previewPaperData.description || '',
                    remark: previewPaperData.remark || '',
                    year: previewPaperData.year,
                    month: previewPaperData.month,
                    organization: previewPaperData.organization,
                    source: previewPaperData.source,
                    nature: previewPaperData.nature,
                    level_text: previewPaperData.level_text,
                    paper_type: previewPaperData.paper_type,
                    duration: previewPaperData.duration || 60
                  }}
                  examQuestions={mapDbQuestionsToStandard(previewQuestions)}
                  currentUser={null}
                  paperId={previewPaperId || undefined}
                  isPreview={true}
                  isEditorPreview={true}
                  onBack={() => setShowPreviewModal(false)}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
